import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';

import { ModalService } from '../../../../shared/modal';
import { ImageHit, ImageResult } from '../../models/image-result.model';
import { IMAGE_TIPS, ImageView, toImageView } from '../image-details';
import { ImageLightbox } from '../image-lightbox/image-lightbox';

/** The include_* toggles the gallery and lightbox can show, for the "turn
 *  these on" tip when no image carries them, each with what it adds.
 *  URL is always there. */
const OPTIONAL_FIELDS: { label: string; adds: string; has: (h: ImageHit) => boolean }[] = [
  { label: 'Metadata', adds: "where each image appeared and what's in it", has: h => !!h.metadata },
  { label: 'Indexation date', adds: 'when Cikisi indexed each image', has: h => !!h.indexationDate },
];

/** Gallery row height in px; rows stretch a little to fill the width. */
const ROW = 180;
const PAGE_SIZE = 40;

/**
 * /image's Response tab: a gallery, not a list of articles. Images only,
 * in justified rows (each at its own proportions, rows the same height);
 * click one for the lightbox with where it appeared and what's in it.
 * Same frame as the other result views: Top sites in the side column
 * filters by the article's site.
 */
@Component({
  selector: 'app-image-results',
  standalone: false,
  templateUrl: './image-results.html',
  styleUrl: './image-results.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageResults {
  readonly result = input.required<ImageResult>();

  private readonly modalService = inject(ModalService);

  protected readonly visible = signal(PAGE_SIZE);
  protected readonly siteFilter = signal<ReadonlySet<string>>(new Set());
  /** Tiles whose stored copy (`url`) failed and are trying the publisher's
   *  own (`metadata.original_url`). */
  protected readonly fallback = signal<ReadonlySet<number>>(new Set());
  /** Tiles where every source failed: a small placeholder instead. */
  protected readonly broken = signal<ReadonlySet<number>>(new Set());
  protected readonly tips = IMAGE_TIPS;
  protected readonly row = ROW;

  private readonly views = computed<ImageView[]>(() => this.result().results.map(toImageView));

  protected readonly filtered = computed(() => {
    const sites = this.siteFilter();
    return sites.size ? this.views().filter(v => sites.has(v.hit.host)) : this.views();
  });

  protected readonly shown = computed(() => this.filtered().slice(0, this.visible()));

  protected readonly missingFields = computed(() => {
    const hits = this.result().results;
    if (!hits.length) return [];
    return OPTIONAL_FIELDS.filter(f => !hits.some(f.has));
  });

  /** Of the images shown, how many couldn't load from any source. */
  protected readonly brokenCount = computed(() => {
    const broken = this.broken();
    return this.shown().filter(v => broken.has(v.index)).length;
  });

  /** The tile's source: the stored copy, or the original once that failed. */
  protected src(v: ImageView): string {
    return this.fallback().has(v.index) ? v.hit.metadata!.originalUrl! : v.hit.url;
  }

  protected open(position: number): void {
    this.modalService.large(ImageLightbox, { data: { views: this.filtered(), start: position } });
  }

  protected toggleSite(host: string): void {
    this.siteFilter.update(set => {
      const next = new Set(set);
      if (next.has(host)) next.delete(host); else next.add(host);
      return next;
    });
    this.visible.set(PAGE_SIZE);
  }

  protected clearSites(): void {
    this.siteFilter.set(new Set());
    this.visible.set(PAGE_SIZE);
  }

  protected showMore(): void {
    this.visible.update(n => n + PAGE_SIZE);
  }

  /** The stored copy failed: try the original if the response has one
   *  (Metadata on); if that fails too, or there's none, show the placeholder. */
  protected imageFailed(v: ImageView): void {
    const original = v.hit.metadata?.originalUrl;
    if (!this.fallback().has(v.index) && original && original !== v.hit.url) {
      this.fallback.update(set => new Set(set).add(v.index));
      return;
    }
    this.broken.update(set => new Set(set).add(v.index));
  }
}
